import React, {useEffect, useState} from 'react';
import {type List} from 'immutable';

import {resource} from '@albert-io/json-api-framework/request/builder';
import {Button, Icon, Text, addGenericErrorToast} from '@albert-io/atomic';
import sessionStore from 'client/Session/SessionStore';
import {getClassroomInvitationsQuery} from 'lib/ClassroomInvitationUtil/ClassroomInvitationUtil';
import {genericMandarkRequest} from 'resources/mandark.resource';

import {usePerformSignupStep} from '../../usePerformSignupStep';
import OnboardingPageLayout from '../OnboardingPageLayout';

interface ClassInvite {
  id: string;
  classroomId: string;
  className: string;
  teacherName: string;
}

interface NewClassInviteStepProps {
  onBack: () => void;
  onSubmit: () => void;
}

function InviteCard({
  invite,
  onAccept,
  isAccepting
}: {
  invite: ClassInvite;
  onAccept: () => void;
  isAccepting: boolean;
}) {
  return (
    <div className='flex flex-wrap items-center gap-x1-half rounded-x1 border border-primary background-subtle p-x2'>
      <div className='flex flex-1 items-center gap-x2 pr-x3'>
        <Icon icon={['far', 'envelope']} />
        <Text>
          You have been invited to <Text bold>{invite.className}</Text> with{' '}
          <Text bold>{invite.teacherName}</Text>
        </Text>
      </div>
      <Button color='primary' size='s' disabled={isAccepting} onClick={onAccept}>
        <Text color='primary-inverse-static'>{isAccepting ? 'Accepting...' : 'Accept'}</Text>
      </Button>
    </div>
  );
}

function EmptyState() {
  return (
    <div className='flex max-w-[516px] flex-col'>
      <Text bold className='mb-x3'>
        We didn&apos;t find any class invites.
      </Text>
      <Text>
        Ask your teacher to invite you in their class roster tab. You can also go back and enter a{' '}
        <Text bold>class code</Text> instead.
      </Text>
    </div>
  );
}

export default function NewClassInviteStep({onBack, onSubmit}: NewClassInviteStepProps) {
  const [invites, setInvites] = useState<ClassInvite[] | undefined>(undefined);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const performSignupStep = usePerformSignupStep();

  const isLoading = invites === undefined;

  useEffect(() => {
    let cancelled = false;

    const fetchInvites = async () => {
      try {
        // fetching student resource directly to get the user email because
        // user email is not accessible via session store for student users
        const studentResource = (await resource('student_v2')
          .mandarkEndpoint(['students_v2', sessionStore.getUserId()])
          .getResourcePromise()) as {getEmail?: () => string} | undefined;
        const email = studentResource?.getEmail?.() ?? '';
        const results = (await getClassroomInvitationsQuery(
          email
        ).getResourcePromise()) as List<any>;

        if (cancelled) return;

        const parsed: ClassInvite[] = results
          .map((invitation: any) => {
            const classroom = invitation.getClassroom();
            const teachers = classroom?.getTeachers?.();
            const teacherName = teachers?.first?.()?.getFullName?.() || 'your teacher';
            return {
              id: invitation.getId(),
              classroomId: classroom?.getId?.() || '',
              className: classroom?.getName?.() || 'a class',
              teacherName
            };
          })
          .toJS();

        setInvites(parsed);
      } catch {
        addGenericErrorToast();
        if (!cancelled) setInvites([]);
      }
    };

    fetchInvites();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAccept = async (invite: ClassInvite) => {
    setAcceptingId(invite.id);
    try {
      await genericMandarkRequest(
        'post',
        {
          resourcePath: ['students_v2', sessionStore.getUserId(), 'relationships', 'classrooms_v1']
        },
        {
          data: [
            {
              type: 'classroom_v1',
              id: invite.classroomId
            }
          ]
        }
      );

      await performSignupStep({
        parentalConsent: {
          consentMethod: 'classroom_invite',
          classroomInvitationId: invite.id
        }
      });
      onSubmit();
    } catch {
      addGenericErrorToast();
    } finally {
      setAcceptingId(null);
    }
  };

  return (
    <OnboardingPageLayout>
      <Button
        color='primary'
        variant='text'
        size='s'
        onClick={onBack}
        className='flex max-w-fit items-center justify-start gap-x1'
      >
        <Icon icon='arrow-left' />
        <Text color='inherit'>Back</Text>
      </Button>

      <Text className='text-xxl font-black'>Accept Class Invite</Text>

      {isLoading ? (
        <Text color='tertiary'>Loading invitations...</Text>
      ) : invites.length > 0 ? (
        <div className='flex flex-col gap-x2'>
          {invites.map((invite) => (
            <InviteCard
              key={invite.id}
              invite={invite}
              onAccept={() => handleAccept(invite)}
              isAccepting={acceptingId === invite.id}
            />
          ))}
        </div>
      ) : (
        <EmptyState />
      )}
    </OnboardingPageLayout>
  );
}
